import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import WarehouseMap from './WarehouseMap';
import Sidebar from './Sidebar';
import AssetInfo from './AssetInfo';
import ForkliftInfo from './ForkliftInfo';
import ReplayTimeline from './ReplayTimeline';
import { useApi } from '../hooks/useApi';
import FeatureGate from './FeatureGate';

// Beacon polling is currently paused - using mock data instead

function MapPage() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const { 
    warehouses, 
    organizations, 
    createWarehouse, 
    checkWarehouseExists, 
    updateBlueprint, 
    updateZones, 
    updateWarehouseGPS, 
    loading,
    updateAssetPosition, 
    getHubs, 
    createHub, 
    updateHubPosition, 
    deleteHub, 
    updateHubName, 
    getTriangulatedBeacons, 
    clearTestBeacons
  } = useApi();
  const [currentWarehouseId, setCurrentWarehouseId] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedForkliftInfo, setSelectedForkliftInfo] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  
  // Handle navigation from AssetManager
  useEffect(() => {
    if (location.state?.assetId) {
      setSelectedAssetId(location.state.assetId);
      if (location.state.warehouseId) {
        setCurrentWarehouseId(location.state.warehouseId);
      }
    }
  }, [location.state]);

  // Listen for trace mode start event from AssetInfo
  useEffect(() => {
    const handleStartTrace = async (event) => {
      const { tagId, startTime, endTime } = event.detail;
      if (!tagId) return;

      try {
        // Fetch trace data from API
        const { getTagTrace } = require('../hooks/useUWBTracking');
        const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'http://localhost:5001' : '');
        const TOKEN_KEY = 'asset_track_token';
        
        const token = localStorage.getItem(TOKEN_KEY);
        const params = new URLSearchParams();
        
        if (startTime) params.append('startTime', startTime);
        if (endTime) params.append('endTime', endTime);
        params.append('limit', 1000);
        params.append('smoothing', 'kalman');

        const response = await fetch(`${API_URL}/api/uwb/tags/${tagId}/trace?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch trace data');
        }

        const data = await response.json();
        
        if (!data.trace || data.trace.length === 0) {
          alert('No trace data available for this time period.');
          return;
        }

        // Calculate duration from timestamps
        const firstTime = new Date(data.trace[0].created_at);
        const lastTime = new Date(data.trace[data.trace.length - 1].created_at);
        const duration = (lastTime - firstTime) / 1000;

        // Debug: Log timestamps to verify
        if (data.trace && data.trace.length > 0) {
          console.log('Trace timestamps:', {
            first: data.trace[0].created_at,
            last: data.trace[data.trace.length - 1].created_at,
            firstDate: new Date(data.trace[0].created_at),
            lastDate: new Date(data.trace[data.trace.length - 1].created_at)
          });
        }

        setTraceMode({
          isActive: true,
          tagId,
          trace: data.trace,
          duration: duration || 60,
          currentTime: 0,
          isPlaying: false,
          playbackSpeed: 1,
          stats: data.stats
        });

      } catch (err) {
        console.error('Failed to load trace:', err);
        alert('Failed to load trace data: ' + err.message);
      }
    };

    window.addEventListener('startTraceMode', handleStartTrace);
    return () => window.removeEventListener('startTraceMode', handleStartTrace);
  }, []);

  // Listen for vision trace mode start event from AssetInfo
  useEffect(() => {
    const handleStartVisionTrace = async (event) => {
      const { assetCode, startTime, endTime } = event.detail;
      if (!assetCode) return;

      try {
        // Fetch vision trace data from API
        const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'http://localhost:5001' : '');
        const TOKEN_KEY = 'asset_track_token';
        
        const token = localStorage.getItem(TOKEN_KEY);
        const params = new URLSearchParams();
        
        if (startTime) params.append('startTime', startTime);
        if (endTime) params.append('endTime', endTime);
        params.append('limit', 1000);

        const response = await fetch(`${API_URL}/api/vision/trace/${assetCode}?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch vision trace data');
        }

        const data = await response.json();
        
        if (!data.trace || data.trace.length === 0) {
          alert('No location history available for this asset in the selected time period.');
          return;
        }

        // Calculate duration from timestamps
        const firstTime = new Date(data.trace[0].created_at);
        const lastTime = new Date(data.trace[data.trace.length - 1].created_at);
        const duration = (lastTime - firstTime) / 1000;

        console.log('Vision trace loaded:', {
          assetCode: data.assetCode,
          points: data.trace.length,
          first: data.trace[0].created_at,
          last: data.trace[data.trace.length - 1].created_at
        });

        setTraceMode({
          isActive: true,
          tagId: data.trace[0].tag_hardware_id, // Use the tag from first detection
          trace: data.trace,
          duration: duration || 60,
          currentTime: 0,
          isPlaying: false,
          playbackSpeed: 1,
          stats: data.stats,
          startTimestamp: firstTime.toISOString(),
          endTimestamp: lastTime.toISOString(),
          assetCode: data.assetCode
        });

      } catch (err) {
        console.error('Failed to load vision trace:', err);
        alert('Failed to load location history: ' + err.message);
      }
    };

    window.addEventListener('startVisionTraceMode', handleStartVisionTrace);
    return () => window.removeEventListener('startVisionTraceMode', handleStartVisionTrace);
  }, []);
  const [isGoogleView, setIsGoogleView] = useState(false);
  const [externalAssets, setExternalAssets] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedRackId, setSelectedRackId] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [warehouseNavTarget, setWarehouseNavTarget] = useState(null); // { id, center: {lat, lng} } for navigating to warehouse
  const [hubs, setHubs] = useState([]); // ESP32 hub/gateway devices
  const [trackedBeacons, setTrackedBeacons] = useState([]); // Triangulated beacon positions
  const isPollingRef = useRef(false); // Prevent multiple simultaneous requests
  
  const previousZoomRef = useRef(null);
  const previousCenterRef = useRef(null);

  // Trace Mode State (Real UWB Historical Data)
  const [traceMode, setTraceMode] = useState({
    isActive: false,
    tagId: null,
    trace: [], // Array of {position: {x, y}, confidence, created_at, interpolated}
    duration: 0,
    currentTime: 0,
    isPlaying: false,
    playbackSpeed: 1,
    stats: null
  });
  const traceIntervalRef = useRef(null);
  
  // Handle warehouse click - navigate to its location on the map
  const handleWarehouseClick = (warehouseId, center) => {
    setWarehouseNavTarget({ id: warehouseId, center, timestamp: Date.now() });
  };

  // Load hubs when component mounts or warehouse changes
  const loadHubsRef = useRef(false); // Prevent multiple simultaneous calls
  const loadHubs = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (loadHubsRef.current) {
      return;
    }
    loadHubsRef.current = true;
    
    try {
      // Load hubs for current warehouse if available
      const allHubs = currentWarehouseId 
        ? await getHubs(currentWarehouseId)
        : await getHubs();
      setHubs(allHubs || []);
    } catch (err) {
      console.error('Failed to load hubs:', err);
      setHubs([]);
    } finally {
      loadHubsRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWarehouseId]); // getHubs intentionally excluded to prevent infinite loops

  useEffect(() => {
    loadHubs();
  }, [loadHubs]);

  // Poll for triangulated beacon positions
  const pollBeacons = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isPollingRef.current) {
      console.debug('Beacon poll skipped - request already in progress');
      return;
    }

    isPollingRef.current = true;
    try {
      const result = await getTriangulatedBeacons(currentWarehouseId);
      if (result && result.beacons) {
        // Debug: Log beacon warehouseIds to check filtering
        if (result.beacons.length > 0) {
          const warehouseIds = [...new Set(result.beacons.map(b => b.warehouseId))];
          console.log(`📍 Triangulated ${result.beacons.length} beacons for warehouse ${currentWarehouseId}`, {
            beaconWarehouseIds: warehouseIds,
            currentWarehouseId,
            beacons: result.beacons.map(b => ({
              id: b.bleTagId,
              pos: b.position,
              warehouseId: b.warehouseId,
              hubs: b.hubCount,
              conf: b.confidence
            }))
          });
        }
        setTrackedBeacons(result.beacons);
      }
    } catch (err) {
      // Silently fail - beacon polling is optional
      if (err.status !== 401) {
        console.warn('Beacon poll failed:', err.message);
      }
    } finally {
      isPollingRef.current = false;
    }
  }, [getTriangulatedBeacons, currentWarehouseId]);

  // Start/stop beacon polling based on warehouse view (currently paused - using mock data)
  useEffect(() => {
    // Beacon polling is paused - using mock data instead
    // Uncomment below to re-enable live beacon polling:
    /*
    // Only poll when in warehouse view (not Google Maps) and have a warehouse selected
    if (!isGoogleView && currentWarehouseId) {
      // Initial poll (with small delay to avoid immediate request)
      const initialTimeout = setTimeout(() => {
        pollBeacons();
      }, 1000);
      
      // Set up interval
      beaconPollRef.current = setInterval(pollBeacons, BEACON_POLL_INTERVAL);
      
      return () => {
        // Clear initial timeout
        clearTimeout(initialTimeout);
        // Clear interval
        if (beaconPollRef.current) {
          clearInterval(beaconPollRef.current);
          beaconPollRef.current = null;
        }
        // Reset polling flag
        isPollingRef.current = false;
      };
    } else {
      // Clear beacons when switching to Google view
      setTrackedBeacons([]);
      isPollingRef.current = false;
    }
    */
    
    // Clear beacons when switching views (using mock data)
    if (isGoogleView) {
      setTrackedBeacons([]);
      isPollingRef.current = false;
    }
  }, [isGoogleView, currentWarehouseId]);

  // Hub handlers
  const handleHubCreate = async (hubData) => {
    try {
      const newHub = await createHub(hubData);
      setHubs(prev => [...prev, newHub]);
      return newHub;
    } catch (err) {
      console.error('Failed to create hub:', err);
      throw err;
    }
  };

  const handleHubPositionUpdate = async (hubId, position) => {
    try {
      const updated = await updateHubPosition(hubId, position);
      setHubs(prev => prev.map(h => h.id === hubId ? { ...h, position } : h));
      return updated;
    } catch (err) {
      console.error('Failed to update hub position:', err);
      throw err;
    }
  };

  const handleHubDelete = async (hubId) => {
    try {
      await deleteHub(hubId);
      setHubs(prev => prev.filter(h => h.id !== hubId));
    } catch (err) {
      console.error('Failed to delete hub:', err);
      throw err;
    }
  };


  const handleClearTestBeacons = async () => {
    try {
      const result = await clearTestBeacons();
      console.log('🧹 Cleared test beacons:', result);
      setTrackedBeacons([]);
    } catch (err) {
      console.error('Failed to clear test beacons:', err);
    }
  };

  // Hub name update handler (for renaming hubs in build mode)
  // NOTE: This updates the display name, NOT the hub_id.
  // The hub_id remains constant and is used to match incoming device data.
  const handleHubNameUpdate = async (hubId, newName) => {
    try {
      const oldHub = hubs.find(h => h.id === hubId);
      const updated = await updateHubName(hubId, newName);
      
      // Update local state with new name
      setHubs(prev => prev.map(h => h.id === hubId ? { ...h, name: updated.name } : h));
      
      // Log the change
      if (oldHub && oldHub.name !== newName) {
        console.log(`✅ Hub display name updated: "${oldHub.name}" → "${newName}"`);
        console.log(`   Hub ID: ${oldHub.hub_id} (unchanged - device still sends this)`);
      }
      
      return updated;
    } catch (err) {
      console.error('Failed to rename hub:', err);
      throw err;
    }
  };

  // Asset position update handler
  const handleAssetPositionUpdate = async (assetId, position, zoneId) => {
    try {
      await updateAssetPosition(assetId, position, zoneId);
    } catch (err) {
      console.error('Failed to update asset position:', err);
      throw err;
    }
  };

  // Set default warehouse when data loads - prefer warehouse with most assets
  useEffect(() => {
    if (!currentWarehouseId && Object.keys(warehouses).length > 0) {
      // Find warehouse with the most assets
      const warehouseEntries = Object.entries(warehouses);
      const warehouseWithMostAssets = warehouseEntries.reduce((max, [id, wh]) => {
        const assetCount = (wh.assets && Array.isArray(wh.assets)) ? wh.assets.length : 0;
        const maxAssetCount = (max.warehouse.assets && Array.isArray(max.warehouse.assets)) ? max.warehouse.assets.length : 0;
        return assetCount > maxAssetCount ? { id, warehouse: wh } : max;
      }, { id: warehouseEntries[0][0], warehouse: warehouseEntries[0][1] });
      
      setCurrentWarehouseId(warehouseWithMostAssets.id);
    }
    // If current warehouse no longer exists (e.g., after deletion), switch to warehouse with most assets
    if (currentWarehouseId && !warehouses[currentWarehouseId] && Object.keys(warehouses).length > 0) {
      const warehouseEntries = Object.entries(warehouses);
      const warehouseWithMostAssets = warehouseEntries.reduce((max, [id, wh]) => {
        const assetCount = (wh.assets && Array.isArray(wh.assets)) ? wh.assets.length : 0;
        const maxAssetCount = (max.warehouse.assets && Array.isArray(max.warehouse.assets)) ? max.warehouse.assets.length : 0;
        return assetCount > maxAssetCount ? { id, warehouse: wh } : max;
      }, { id: warehouseEntries[0][0], warehouse: warehouseEntries[0][1] });
      
      setCurrentWarehouseId(warehouseWithMostAssets.id);
    }
  }, [warehouses, currentWarehouseId]);

  const currentData = warehouses[currentWarehouseId] || null;

  // Calculate asset statistics for top big numbers - based on current warehouse only
  const assetStats = useMemo(() => {
    let inHouse = 0;
    let atPartnerFacility = 0;
    let mia = 0;

    // Only count assets from the current warehouse
    if (currentData && currentData.assets) {
      currentData.assets.forEach(asset => {
        const status = asset.status || 'Active';

        // Count MIA (Missing status)
        if (status === 'Missing') {
          mia++;
        } else if (status === 'In-Transit') {
          // Assets that are in transit (at partner facility)
          atPartnerFacility++;
        } else if (status === 'Active') {
          // Active assets in the warehouse (in house)
          inHouse++;
        }
      });
    }

    return { inHouse, atPartnerFacility, mia };
  }, [currentData]);

  const handleCreateWarehouse = async (name, layoutData, options = {}) => {
    const newId = await createWarehouse(name, layoutData, options);
    if (newId) {
      setCurrentWarehouseId(newId);
      setIsGoogleView(false);
    }
    return newId;
  };

  const handleBlueprintUpdate = (updatedBlueprint) => {
    if (currentWarehouseId) {
      updateBlueprint(currentWarehouseId, updatedBlueprint);
    }
  };

  const handleZoneUpdate = (updatedZones) => {
    if (currentWarehouseId) {
      updateZones(currentWarehouseId, updatedZones);
    }
  };

  const handleWarehouseChange = (id) => {
    setCurrentWarehouseId(id);
    clearAllSelections();
    setIsGoogleView(false);
  };

  // Clear rack selection when zone changes
  const clearAllSelections = () => {
    setSelectedAssetId(null);
    setSelectedZoneId(null);
    setSelectedRackId(null);
    setSelectedAssetType(null);
  };

  const handleZoneSelect = (zoneId) => {
    clearAllSelections();
    setSelectedZoneId(zoneId);
  };

  const handleRackSelect = (rackId) => {
    clearAllSelections();
    setSelectedRackId(rackId);
  };

  const handleAssetSelect = (assetId) => {
    clearAllSelections();
    setSelectedAssetId(assetId);
  };

  const handleAssetTypeSelect = (assetType) => {
    clearAllSelections();
    setSelectedAssetType(assetType);
  };

  // Trace Mode Handlers
  const handleTracePlay = () => {
    setTraceMode(prev => ({ ...prev, isPlaying: true }));
  };

  const handleTracePause = () => {
    setTraceMode(prev => ({ ...prev, isPlaying: false }));
  };

  const handleTraceStop = () => {
    if (traceIntervalRef.current) {
      clearInterval(traceIntervalRef.current);
      traceIntervalRef.current = null;
    }

    setTraceMode({
      isActive: false,
      tagId: null,
      trace: [],
      duration: 0,
      currentTime: 0,
      isPlaying: false,
      playbackSpeed: 1,
      stats: null
    });
  };

  const handleTraceTimeChange = (newTime) => {
    setTraceMode(prev => ({ ...prev, currentTime: newTime }));
  };

  // Trace mode playback effect
  useEffect(() => {
    if (traceMode.isActive && traceMode.isPlaying && traceMode.trace.length > 0) {
      const interval = setInterval(() => {
        setTraceMode(prev => {
          const newTime = prev.currentTime + (0.1 * prev.playbackSpeed);
          if (newTime >= prev.duration) {
            return { ...prev, currentTime: prev.duration, isPlaying: false };
          }
          return { ...prev, currentTime: newTime };
        });
      }, 100);

      traceIntervalRef.current = interval;
      return () => clearInterval(interval);
    } else if (traceIntervalRef.current) {
      clearInterval(traceIntervalRef.current);
      traceIntervalRef.current = null;
    }
  }, [traceMode.isActive, traceMode.isPlaying, traceMode.playbackSpeed, traceMode.duration, traceMode.trace.length]);

  if (loading && !currentData) {
    return <div className={`h-full w-full flex items-center justify-center ${isDark ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>Loading warehouse data...</div>;
  }

  return (
    <FeatureGate 
      user={user} 
      featureKey="live_map"
      blur={true}
      showUpgradePrompt={true}
      upgradePlanName="Starter"
      customMessage="Live map access is required. Contact your administrator."
    >
      <div className="h-full w-full relative overflow-hidden">
      <WarehouseMap
        key={currentWarehouseId}
        data={currentData || { zones: [], assets: [] }}
        selectedAssetId={selectedAssetId}
        selectedZoneId={selectedZoneId}
        selectedRackId={selectedRackId}
        selectedAssetType={selectedAssetType}
        onZoneSelect={handleZoneSelect}
        onAssetSelect={handleAssetSelect}
        onRackSelect={handleRackSelect}
        onViewChange={(enabled, assets) => { setIsGoogleView(enabled); setExternalAssets(assets || []); }}
        currentWarehouseId={currentWarehouseId}
        warehouses={warehouses}
        organizations={organizations}
        onWarehouseChange={handleWarehouseChange}
        onCreateWarehouse={handleCreateWarehouse}
        checkWarehouseExists={checkWarehouseExists}
        onZoneUpdate={handleZoneUpdate}
        onBlueprintUpdate={handleBlueprintUpdate}
        onUpdateWarehouseGPS={updateWarehouseGPS}
        onWarehouseClick={handleWarehouseClick}
        warehouseNavTarget={warehouseNavTarget}
        onAssetPositionUpdate={handleAssetPositionUpdate}
        hubs={hubs}
        onHubCreate={handleHubCreate}
        onHubPositionUpdate={handleHubPositionUpdate}
        onHubDelete={handleHubDelete}
        onHubNameUpdate={handleHubNameUpdate}
        trackedBeacons={trackedBeacons}
        onClearTestBeacons={handleClearTestBeacons}
        traceMode={traceMode}
        onTraceTimeChange={handleTraceTimeChange}
        selectedForkliftInfo={selectedForkliftInfo}
        onForkliftInfoChange={setSelectedForkliftInfo}
      />

      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setSidebarVisible(!sidebarVisible)}
        className={`fixed right-4 top-20 z-40 p-2 rounded-lg shadow-lg transition-all ${
          isDark 
            ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700' 
            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
        }`}
        title={sidebarVisible ? 'Hide Sidebar' : 'Show Sidebar'}
      >
        {sidebarVisible ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        )}
      </button>

      {sidebarVisible && (
        <div className="pointer-events-none absolute inset-0 p-4 sm:p-6 md:p-8 flex justify-end items-start z-30">
          <div className="pointer-events-auto max-h-full max-w-[380px] w-full flex flex-col gap-4 overflow-y-auto no-scrollbar">
            <Sidebar
              data={isGoogleView ? { ...(currentData || { zones: [], assets: [] }), assets: externalAssets } : (currentData || { zones: [], assets: [] })}
              isGoogleView={isGoogleView}
              selectedZoneId={selectedZoneId}
              selectedRackId={selectedRackId}
              selectedAssetType={selectedAssetType}
              onZoneSelect={handleZoneSelect}
              onAssetSelect={handleAssetSelect}
              onRackSelect={handleRackSelect}
              onAssetTypeSelect={handleAssetTypeSelect}
              warehouses={warehouses}
              onWarehouseClick={handleWarehouseClick}
              assetStats={!isGoogleView ? assetStats : null}
            />
            <AssetInfo
              data={isGoogleView ? { ...(currentData || { zones: [], assets: [] }), assets: externalAssets } : (currentData || { zones: [], assets: [] })}
              selectedAssetId={selectedAssetId}
            />
            {/* Forklift Info Popup - appears below sidebar when forklift is selected */}
            <ForkliftInfo
              forkliftInfo={selectedForkliftInfo}
              onClose={() => setSelectedForkliftInfo(null)}
            />
          </div>
        </div>
      )}

      {/* Trace Timeline - Only show when trace mode is active */}
      {traceMode.isActive && traceMode.trace.length > 0 && (
        <ReplayTimeline
          duration={traceMode.duration}
          currentTime={traceMode.currentTime}
          onTimeChange={handleTraceTimeChange}
          onPlay={handleTracePlay}
          onPause={handleTracePause}
          onStop={handleTraceStop}
          isPlaying={traceMode.isPlaying}
          isFastForwarding={false}
          title="Historical Trace"
          startTimestamp={traceMode.trace[0]?.created_at}
          endTimestamp={traceMode.trace[traceMode.trace.length - 1]?.created_at}
        />
      )}
      </div>
    </FeatureGate>
  );
}

export default MapPage;
